import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useReferenceStore, ReferenceOption, ReferenceStore } from '../store/useReferenceStore';
import { X, Save, Plus, ChevronDown, Pencil, Trash2, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import ConfirmDialog from './ConfirmDialog';

interface ReferenceDropdownProps {
  label: string;
  category: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
}

const ReferenceDropdown: React.FC<ReferenceDropdownProps> = ({
  label,
  category,
  value,
  onChange,
  required = false
}) => {
  const options = useReferenceStore((state: ReferenceStore) => state.options);
  const addOption = useReferenceStore((state: ReferenceStore) => state.addOption);
  const renameOption = useReferenceStore((state: ReferenceStore) => state.renameOption);
  const deleteOption = useReferenceStore((state: ReferenceStore) => state.deleteOption);

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  // Option being renamed inline (its key) + the working text.
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editingLabel, setEditingLabel] = useState('');
  const [renamingBusy, setRenamingBusy] = useState(false);
  // Option queued for deletion — held until the confirm dialog is answered.
  const [pendingDelete, setPendingDelete] = useState<ReferenceOption | null>(null);
  const [coords, setCoords] = useState({ top: 0, bottom: 0, left: 0, width: 0, maxWidth: 0, maxHeight: 300, openUp: false });
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Comfortable minimum for the portaled list so names stay readable even when
  // the field sits in a narrow column.
  const DROPDOWN_MIN_WIDTH = 240;

  const list = options[category] || [];

  // Sync searchTerm with initial value
  useEffect(() => {
    setSearchTerm(value || '');
  }, [value]);

  // Leaving the dropdown abandons any half-finished inline rename.
  useEffect(() => {
    if (!isOpen) { setEditingKey(null); setEditingLabel(''); setHoveredKey(null); }
  }, [isOpen]);

  // The menu is portaled to <body> (see render below) so it floats above the
  // modal instead of being clipped inside its scroll area. Since it's no longer
  // a DOM descendant of containerRef, its position is computed from the input's
  // viewport rect and kept in sync while open.
  useEffect(() => {
    if (!isOpen) return;
    const updatePosition = () => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const GAP = 4;
      const EDGE = 8;          // keep clear of the viewport edge
      const PREFERRED = 300;   // the menu's usual height
      const spaceBelow = window.innerHeight - rect.bottom - GAP - EDGE;
      const spaceAbove = rect.top - GAP - EDGE;
      // A field near the bottom of the window — the last row of a tall modal,
      // typically — has nowhere to drop into, so the menu opens upward instead
      // of running off the screen. Whichever side is used, maxHeight is capped
      // to the room actually available so it can never overflow the viewport.
      const openUp = spaceBelow < Math.min(PREFERRED, 220) && spaceAbove > spaceBelow;
      setCoords({
        top: rect.bottom + GAP,
        bottom: window.innerHeight - rect.top + GAP,
        left: rect.left,
        width: rect.width,
        // maxWidth caps how far the list may grow right before it would run off
        // screen; past that, long names wrap instead.
        maxWidth: window.innerWidth - rect.left - EDGE,
        maxHeight: Math.max(140, Math.min(PREFERRED, openUp ? spaceAbove : spaceBelow)),
        openUp,
      });
    };
    updatePosition();
    // capture:true catches scrolling in any nested container, not just window —
    // the modal body being the one that matters here.
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  // Close dropdown on click outside — of the field *or* the portaled menu.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideContainer = !!containerRef.current?.contains(target);
      const insideDropdown = !!dropdownRef.current?.contains(target);
      if (!insideContainer && !insideDropdown) {
        setIsOpen(false);
        setSearchTerm(value || ''); // Revert to actual value if user didn't select anything
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [value]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return list;
    return list.filter(opt => 
      opt.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, list]);

  const exactMatchExists = list.some(
    opt => opt.label.toLowerCase().trim() === searchTerm.toLowerCase().trim()
  );

  // Add + select in one go. Shared by the footer row and the Enter key so both
  // land on exactly the same behaviour.
  const commitNew = async (name: string) => {
    const trimmed = name.trim();
    if (!trimmed || isSaving) return;
    setIsSaving(true);
    try {
      await addOption(category, trimmed);
      onChange(trimmed);
      setIsOpen(false);
      toast.success('Saved to list');
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveToDatabase = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Nothing typed yet — point at the search box rather than doing nothing
    // silently, since the row is now always on screen.
    if (!searchTerm.trim()) { inputRef.current?.focus(); return; }
    commitNew(searchTerm);
  };

  // Enter commits what's typed without reaching for the mouse: a name already
  // on the list is selected (so Enter can never mint a duplicate), anything
  // else is added — the same thing the footer row does.
  //
  // Escape closes the dropdown and stops there, rather than also reaching the
  // modal behind it and closing the whole registration form.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      if (!isOpen) return;
      e.preventDefault();
      e.stopPropagation();
      setIsOpen(false);
      setSearchTerm(value || '');
      return;
    }
    if (e.key !== 'Enter') return;
    // Never let it submit the surrounding form.
    e.preventDefault();
    e.stopPropagation();
    const typed = searchTerm.trim();
    if (!typed) { setIsOpen(false); return; }
    const match = list.find(opt => opt.label.toLowerCase() === typed.toLowerCase());
    if (match) {
      onChange(match.label);
      setIsOpen(false);
      return;
    }
    commitNew(typed);
  };

  const beginRename = (opt: ReferenceOption) => {
    setEditingKey(opt.key);
    setEditingLabel(opt.label);
  };

  const cancelRename = () => {
    setEditingKey(null);
    setEditingLabel('');
  };

  const commitRename = async (opt: ReferenceOption) => {
    const next = editingLabel.trim();
    if (!next || next === opt.label) { cancelRename(); return; }
    setRenamingBusy(true);
    try {
      await renameOption(category, opt.key, next);
      // The field stores the label, not the key — so a rename of the option
      // that's currently selected has to be carried across, or the field would
      // be left holding a name that's no longer on the list.
      if (value === opt.label) onChange(next);
      toast.success('Renamed');
      cancelRename();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to rename');
    } finally {
      setRenamingBusy(false);
    }
  };

  const confirmDelete = async () => {
    const opt = pendingDelete;
    setPendingDelete(null);
    if (!opt) return;
    try {
      await deleteOption(category, opt.key);
      if (value === opt.label) onChange('');
      toast.success('Removed from list');
    } catch {
      toast.error('Failed to remove');
    }
  };

  return (
    <div ref={containerRef} style={{ display: 'flex', flexDirection: 'column', gap: '4px', position: 'relative', width: '100%' }}>
      <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
        {label} {required && '*'}
      </label>

      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <input
          ref={inputRef}
          onKeyDown={handleKeyDown}
          style={{
            padding: '0.6rem 2.5rem 0.6rem 0.75rem',
            borderRadius: '6px',
            border: `1px solid ${isOpen ? '#3b82f6' : '#cbd5e1'}`,
            fontSize: '14px',
            outline: 'none',
            backgroundColor: '#fff',
            width: '100%',
            height: '40px',
            boxSizing: 'border-box',
            transition: 'border-color 0.2s'
          }}
          placeholder={`Search or type ${label.toLowerCase()}...`}
          value={searchTerm}
          onFocus={() => setIsOpen(true)}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
        />
        
        {/* Toggle Icons */}
        <div style={{ position: 'absolute', right: '8px', display: 'flex', gap: '4px', color: '#94a3b8' }}>
          {searchTerm && (
            <X size={16} style={{ cursor: 'pointer' }} onClick={() => { onChange(''); setSearchTerm(''); }} />
          )}
          <ChevronDown size={16} style={{ cursor: 'pointer', transform: isOpen ? 'rotate(180deg)' : 'none' }} onClick={() => setIsOpen(!isOpen)} />
        </div>
      </div>

      {/* DROPDOWN MENU — portaled to <body> so it floats above the modal
          instead of being clipped inside the modal's own scroll area. */}
      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          // Marks this as dropdown chrome living outside its owner in the DOM,
          // for screens whose own click-outside handlers need to ignore it.
          data-portal-dropdown="true"
          // A portal is outside its owner in the DOM but NOT in the React tree —
          // clicks still bubble to React ancestors. The patient modals close on
          // a click at their overlay, so without this, picking an option would
          // shut the whole registration form.
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            // Anchored to whichever edge it opens from, so an upward menu grows
            // away from the field rather than over it.
            ...(coords.openUp ? { bottom: coords.bottom } : { top: coords.top }),
            left: coords.left,
            // As wide as the content needs, but at least the field width / a
            // readable minimum, and never past the screen edge.
            width: 'max-content',
            minWidth: Math.max(coords.width, DROPDOWN_MIN_WIDTH),
            maxWidth: Math.max(coords.maxWidth, DROPDOWN_MIN_WIDTH),
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
            border: '1px solid #e2e8f0',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            // Capped to the room actually available on the chosen side.
            maxHeight: coords.maxHeight
          }}
        >
        {/* Scrollable options — capped so the "add" row below stays pinned and
            visible without scrolling past the whole list. */}
        <div style={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>
          {filteredOptions.map((opt) => {
            const isSelected = value === opt.label;
            const isEditing = editingKey === opt.key;
            // Actions stay hidden until the row is hovered, so the list reads as
            // a list of choices rather than a row of buttons.
            const showActions = hoveredKey === opt.key || isEditing;
            return (
              <div
                key={opt.key}
                onMouseEnter={() => setHoveredKey(opt.key)}
                onMouseLeave={() => setHoveredKey(prev => (prev === opt.key ? null : prev))}
                style={{
                  padding: '10px 10px 10px 12px',
                  fontSize: '14px',
                  borderBottom: '1px solid #f1f5f9',
                  backgroundColor: isEditing ? '#fffbeb' : isSelected ? '#eff6ff' : (hoveredKey === opt.key ? '#f8fafc' : 'transparent'),
                  color: isSelected ? '#1d4ed8' : '#1e293b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                {isEditing ? (
                  <>
                    {/* Inline rename — fix a typo without leaving the dropdown. */}
                    <input
                      autoFocus
                      value={editingLabel}
                      onChange={(e) => setEditingLabel(e.target.value)}
                      onMouseDown={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); commitRename(opt); }
                        if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); cancelRename(); }
                      }}
                      style={{ flex: 1, minWidth: 0, padding: '3px 6px', border: '1px solid #93c5fd', borderRadius: '4px', fontSize: '14px', outline: 'none' }}
                    />
                    <Check
                      size={15}
                      style={{ cursor: renamingBusy ? 'wait' : 'pointer', color: '#16a34a', flexShrink: 0 }}
                      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); commitRename(opt); }}
                    />
                    <X
                      size={15}
                      style={{ cursor: 'pointer', color: '#94a3b8', flexShrink: 0 }}
                      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); cancelRename(); }}
                    />
                  </>
                ) : (
                  <>
                    <span
                      onMouseDown={() => { onChange(opt.label); setIsOpen(false); }}
                      style={{ flex: 1, minWidth: 0, cursor: 'pointer', overflowWrap: 'anywhere' }}
                    >
                      {opt.label}
                    </span>
                    <span
                      title="Rename"
                      style={{ display: 'inline-flex', flexShrink: 0, visibility: showActions ? 'visible' : 'hidden' }}
                      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); beginRename(opt); }}
                    >
                      <Pencil size={14} style={{ cursor: 'pointer', color: '#64748b' }} />
                    </span>
                    <span
                      title="Delete"
                      style={{ display: 'inline-flex', flexShrink: 0, visibility: showActions ? 'visible' : 'hidden' }}
                      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setPendingDelete(opt); }}
                    >
                      <Trash2 size={14} style={{ cursor: 'pointer', color: '#ef4444' }} />
                    </span>
                  </>
                )}
              </div>
            );
          })}

          {filteredOptions.length === 0 && !searchTerm && (
            <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
              No options available. Type to add.
            </div>
          )}
        </div>

        {/* PERMANENT ADD BUTTON — pinned below the scroll area so it's always
            in view, and shown even before anything is typed so the way to add
            an entry is found rather than guessed at. */}
        {!exactMatchExists && (
          <div
            onMouseDown={handleSaveToDatabase}
            style={{
              padding: '12px',
              backgroundColor: '#eff6ff',
              cursor: isSaving ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
              color: '#2563eb',
              fontWeight: 600,
              fontSize: '13px',
              borderTop: '1px solid #e2e8f0',
              borderRadius: '0 0 8px 8px',
              flex: '0 0 auto'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
              <Plus size={16} style={{ flexShrink: 0 }} />
              <span style={{ overflowWrap: 'anywhere' }}>
                {searchTerm.trim() ? `Add "${searchTerm.trim()}" permanently` : `Add new ${(label || category).toLowerCase().replace(/_/g, ' ')}`}
              </span>
            </div>
            {/* Says what Enter will do, so the shortcut is found not guessed. */}
            {searchTerm.trim() ? (
              <span style={{
                flexShrink: 0, fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.02em',
                color: '#1d4ed8', background: '#dbeafe', border: '1px solid #bfdbfe',
                borderRadius: '4px', padding: '1px 5px', whiteSpace: 'nowrap'
              }}>
                ↵ Enter
              </span>
            ) : (
              <Save size={14} style={{ flexShrink: 0 }} />
            )}
          </div>
        )}
        </div>,
        document.body
      )}

      {/* Deleting only takes the entry off the list — records that already hold
          the text keep it, so say so before the click rather than after.
          Some callers pass an empty label (the field is titled by its column
          header instead), so the title falls back to the category name. */}
      <ConfirmDialog
        open={!!pendingDelete}
        danger
        title={`Delete "${pendingDelete?.label || ''}" from the ${(label || category).toLowerCase().replace(/_/g, ' ')} list?`}
        message="It will no longer be offered in this dropdown."
        points={[
          'Records already saved with this text keep it — nothing existing changes.',
          'It can be added back later by typing it in again.',
        ]}
        confirmLabel="Delete"
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
};

export default ReferenceDropdown;